import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Bot, CheckCircle2, Download, Flag, HelpCircle, Link as LinkIcon, MailWarning, ShieldCheck, ShieldX, XCircle } from 'lucide-react';
import Header from '../components/layout/Header';
import { api } from '../services/api';

/* ────────────────────────────────────────────────
   Configuration des verdicts — ADAPTÉ PAR TYPE
   ──────────────────────────────────────────────── */
function getVerdictConfig(verdict, type) {
  const isEmail = type === 'email' || type === 'message';
  // eslint-disable-next-line no-unused-vars
  const _subject = isEmail ? 'Ce message' : 'Ce lien';

  const configs = {
    danger: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-600',
      scoreBg: 'bg-red-600',
      Icon: ShieldX,
      label: '🚨 ATTENTION — DANGER',
      message: isEmail
        ? 'Ce message est suspect. Il présente des signes d\'arnaque ou de fraude.'
        : 'Ce lien est dangereux. Il peut voler vos informations personnelles, vos mots de passe ou votre argent.',
      advice: isEmail
        ? '❌ Ne répondez pas à ce message. Ne cliquez sur aucun lien. Ne donnez aucune information.'
        : '❌ Ne cliquez sur aucun lien. Ne donnez aucune information. Fermez la page immédiatement.',
      emoji: '🔴',
    },
    suspect: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-600',
      scoreBg: 'bg-amber-500',
      Icon: AlertTriangle,
      label: '⚠️ PRUDENCE',
      message: isEmail
        ? 'Ce message présente des signes suspects. Restez vigilant.'
        : 'Ce lien présente des signes suspects. Il pourrait être une arnaque.',
      advice: isEmail
        ? '⚠️ Soyez prudent. Vérifiez l\'expéditeur avant de répondre ou de cliquer sur quoi que ce soit.'
        : '⚠️ Soyez très prudent. Ne donnez pas vos informations personnelles sur ce site.',
      emoji: '🟠',
    },
    safe: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-600',
      scoreBg: 'bg-emerald-500',
      Icon: ShieldCheck,
      label: isEmail ? '✅ CE MESSAGE EST SÛR' : '✅ CE LIEN EST SÛR',
      message: isEmail
        ? 'Nous avons vérifié ce message pour vous. Aucun signe d\'arnaque détecté.'
        : 'Nous avons vérifié ce lien pour vous. Il ne présente pas de danger connu.',
      advice: '✅ Vous pouvez continuer en toute tranquillité.',
      emoji: '🟢',
    },
  };
  return configs[verdict] || configs.safe;
}

/* ────────────────────────────────────────────────
   Score visuel — jauge simple
   ──────────────────────────────────────────────── */
function ScoreGauge({ score, config }) {
  const level = score >= 70 ? 'Risque élevé' : score >= 40 ? 'Risque moyen' : 'Risque faible';
  return (
    <div className="mt-6 max-w-xs mx-auto">
      <div className="flex justify-between text-sm font-medium mb-2">
        <span className="text-gray-500">Niveau de risque</span>
        <span className={config.text}>{level}</span>
      </div>
      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${config.scoreBg} rounded-full transition-all duration-700`}
          style={{ width: `${Math.max(score, 3)}%` }}
        />
      </div>
      <p className="mt-1 text-center text-xs text-gray-400">{score} sur 100</p>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Conseil d'action — SPÉCIFIQUE AU DANGER
   ──────────────────────────────────────────────── */
function AdviceSection({ advice, config }) {
  if (!advice || advice.length === 0) return null;
  return (
    <div className="mx-4 lg:mx-0 mt-5 space-y-4">
      <p className="text-xl font-bold text-gray-900 px-1">💡 Que dois-je faire ?</p>
      {advice.map((block) => (
        <div key={block.title} className={`rounded-2xl ${config.bg} border ${config.border} px-5 py-4`}>
          <p className="font-bold text-gray-900 text-base">{block.title}</p>
          <ul className="mt-3 space-y-2">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-gray-700 text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────
   Raisons — langage humain
   ──────────────────────────────────────────────── */
function ReasonCard({ reason }) {
  // Déterminer l'icône par le contenu de la raison, pas par le verdict global
  const isPositive = reason.title.startsWith('✅');
  const isDanger = reason.title.startsWith('🚨');
  // ⚠️ = warning, tout autre = neutre
  const iconColor = isDanger ? 'text-red-400 bg-red-50'
    : isPositive ? 'text-emerald-400 bg-emerald-50'
    : 'text-amber-400 bg-amber-50';
  const Icon = isDanger ? XCircle : isPositive ? CheckCircle2 : AlertTriangle;
  return (
    <div className="flex gap-4 py-4 border-b border-gray-100 last:border-0">
      <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <p className="font-semibold text-gray-900">{reason.title}</p>
        <p className="text-sm text-gray-500 leading-relaxed mt-1">{reason.desc}</p>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Résultat URL
   ──────────────────────────────────────────────── */
function UrlResult({ data, config }) {
  const reasons = data.reasons?.length ? data.reasons : [{ title: 'Vérification terminée', desc: 'SENTINELLE a analysé ce lien pour vous.' }];
  const VerdictIcon = config.Icon;

  return (
    <>
      {/* En-tête du verdict */}
      <section className={`${config.bg} border-b ${config.border} rounded-b-3xl lg:rounded-3xl px-5 pt-10 pb-8 text-center`}>
        <div className={`w-20 h-20 rounded-3xl mx-auto flex items-center justify-center ${config.text} bg-white shadow-sm`}>
          <VerdictIcon className="w-11 h-11" />
        </div>
        <h1 className={`mt-6 font-extrabold text-2xl md:text-3xl ${config.text}`}>{config.label}</h1>
        <p className="mt-4 text-base text-gray-700 leading-relaxed max-w-md mx-auto">{config.message}</p>
        <ScoreGauge score={data.score} config={config} />
      </section>

      {/* Ce qui a été analysé */}
      <div className="mx-4 lg:mx-0 -mt-4 card flex gap-3 items-center shadow-sm">
        <span className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400">
          <LinkIcon className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Lien vérifié</p>
          <p className="font-mono text-sm text-gray-600 break-all">{data.url || data.label || 'Élément analysé'}</p>
        </div>
      </div>

      {/* Conseils spécifiques */}
      <AdviceSection advice={data.advice} config={config} />

      {/* Raisons expliquées simplement */}
      <div className="mx-4 lg:mx-0 mt-4 card p-0 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-gray-400" />
          <h2 className="font-bold text-lg text-gray-900">Ce que nous avons trouvé</h2>
        </div>
        <div className="px-5">
          {reasons.map((reason) => (
            <ReasonCard key={reason.title} reason={reason} />
          ))}
        </div>
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────
   Résultat Email
   ──────────────────────────────────────────────── */
function EmailResult({ data, config }) {
  const VerdictIcon = config.Icon;
  const sender = data.sender || {};
  const reasons = data.reasons?.length ? data.reasons : [{ title: 'Vérification terminée', desc: 'SENTINELLE a analysé cet email pour vous.' }];

  return (
    <>
      {/* Verdict */}
      <section className={`${config.bg} border ${config.border} rounded-3xl mx-4 lg:mx-0 mt-4 lg:mt-0 px-5 py-8 text-center`}>
        <div className={`w-20 h-20 rounded-2xl mx-auto flex items-center justify-center ${config.text} bg-white shadow-sm`}>
          <VerdictIcon className="w-11 h-11" />
        </div>
        <h1 className={`mt-6 font-extrabold text-2xl ${config.text}`}>{config.label}</h1>
        <p className="mt-4 text-gray-700 leading-relaxed">{config.message}</p>
        <ScoreGauge score={data.score} config={config} />
      </section>

      {/* Conseils spécifiques */}
      <AdviceSection advice={data.advice} config={config} />

      {/* Qui a envoyé cet email */}
      <div className="mx-4 lg:mx-0 mt-5 card">
        <h2 className="font-bold text-lg text-gray-900 flex items-center gap-2 mb-3">
          <MailWarning className="w-5 h-5 text-gray-400" />
          Qui a envoyé cet email ?
        </h2>
        <div className="space-y-3">
          <InfoRow label="Il dit s'appeler" value={sender.displayName || 'Inconnu'} />
          <InfoRow label="Son adresse email" value={sender.email || 'Non disponible'} />
        </div>
      </div>

      {/* Alertes simples */}
      {data.contentWarnings?.length > 0 && (
        <div className="mx-4 lg:mx-0 mt-4 card bg-amber-50 border border-amber-200">
          <p className="font-bold text-gray-900 mb-2">⚠️ Points d'attention</p>
          <ul className="space-y-2">
            {data.contentWarnings.map((w) => (
              <li key={w} className="flex items-start gap-2 text-gray-700">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Détails */}
      <div className="mx-4 lg:mx-0 mt-4 card p-0 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-gray-400" />
          <h2 className="font-bold text-lg text-gray-900">Ce qu'on a trouvé</h2>
        </div>
        <div className="px-5">
          {reasons.map((reason) => (
            <ReasonCard key={reason.title} reason={reason} />
          ))}
        </div>
      </div>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="mt-0.5 text-gray-700 break-all">{value}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Actions
   ──────────────────────────────────────────────── */
function openChatWithContext(data) {
  window.dispatchEvent(new CustomEvent('sentinelle:open-chat', {
    detail: { context: data, message: 'Explique ce score' },
  }));
}

function Actions({ navigate, data }) {
  const isDangerous = data.verdict === 'danger' || data.verdict === 'suspect';
  return (
    <div className="fixed-shell bottom-16 px-4 md:px-10 pb-3 bg-surface-page border-t border-gray-100 pt-3 lg:sticky lg:top-6 lg:left-auto lg:right-auto lg:bottom-auto lg:mx-0 lg:w-auto lg:max-w-none lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
      <div className="lg:card lg:p-5">
        {isDangerous ? (
          <>
            <p className="hidden lg:block font-bold text-red-600 mb-3">🚨 Protegez-vous</p>
            <button type="button" onClick={() => navigate('/report', { state: { analysis: data } })} className="btn-primary bg-red-600 hover:bg-red-700" aria-label="Signaler ce danger aux autorites">
              <Flag className="w-5 h-5" />Signaler ce danger
            </button>
          </>
        ) : (
          <>
            <p className="hidden lg:block font-bold text-emerald-600 mb-3">✅ Tout va bien</p>
            <button type="button" onClick={() => navigate('/analyze')} className="btn-primary bg-emerald-600 hover:bg-emerald-700">
              <ArrowRight className="w-5 h-5" />Verifier autre chose
            </button>
          </>
        )}

        <button type="button" onClick={() => api.downloadReportPdf(data)} className="btn-secondary mt-2" aria-label="Telecharger le rapport PDF">
          <Download className="w-5 h-5" />Garder une preuve (PDF)
        </button>

        <div className="flex items-center justify-center gap-4 mt-2">
          <button type="button" id="sentinelle-ask-ai-btn" onClick={() => openChatWithContext(data)} className="text-indigo-600 text-sm font-semibold flex items-center gap-1.5 active:scale-95 transition-transform">
            <Bot className="w-4 h-4" />Demander a l'IA
          </button>
          {isDangerous && (
            <>
              <span className="text-gray-300">|</span>
              <button type="button" onClick={() => navigate('/analyze')} className="text-gray-500 text-sm font-medium active:scale-95 transition-transform">Autre analyse</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   Page principale
   ──────────────────────────────────────────────── */
export default function ResultsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const data = state?.data || { type: 'url', score: 0, verdict: 'safe', reasons: [], label: 'Analyse' };
  const config = getVerdictConfig(data.verdict, data.type);

  return (
    <div className="page-container bg-white lg:bg-surface-page">
      <Header />
      <main className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6 lg:items-start lg:px-4 lg:pt-6">
        <section className="pb-44 lg:pb-0">{data.type === 'email' ? <EmailResult data={data} config={config} /> : <UrlResult data={data} config={config} />}</section>
        <Actions navigate={navigate} data={data} />
      </main>
    </div>
  );
}
